import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Slider } from '@/components/ui/slider';
import { Loader2, Clock, Calendar } from 'lucide-react';
import { useCreateSchedule } from '@/service/gpu-instance/volume-hook';
import type { Volume, ScheduleFrequency } from '@/service/gpu-instance/volume-type';
import { toast } from 'sonner';

interface ScheduleSnapshotDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  volume: Volume | null;
  onSuccess?: () => void;
}

const frequencyOptions: { value: ScheduleFrequency; label: string; description: string }[] = [
  { value: 'hourly', label: 'Hourly', description: 'Every hour on the hour' },
  { value: 'daily', label: 'Daily', description: 'Once per day at midnight UTC' },
  { value: 'weekly', label: 'Weekly', description: 'Once per week on Sunday at midnight UTC' },
  { value: 'custom', label: 'Custom', description: 'Define your own cron expression' },
];

export const ScheduleSnapshotDialog = ({
  open,
  onOpenChange,
  volume,
  onSuccess,
}: ScheduleSnapshotDialogProps) => {
  const [name, setName] = useState('');
  const [frequency, setFrequency] = useState<ScheduleFrequency>('daily');
  const [cronExpression, setCronExpression] = useState('0 0 * * *');
  const [retentionCount, setRetentionCount] = useState(7);
  const [snapshotPrefix, setSnapshotPrefix] = useState('');

  const createSchedule = useCreateSchedule();

  if (!volume) return null;

  const validateName = (value: string) => {
    return /^[a-zA-Z0-9][a-zA-Z0-9\s-]*[a-zA-Z0-9]$|^[a-zA-Z0-9]$/.test(value);
  };

  const validateCron = (cron: string) => {
    const parts = cron.split(' ');
    return parts.length === 5 && parts.every((part) => /^[\d*,/-]+$/.test(part));
  };

  const isValid =
    name.length >= 1 &&
    validateName(name) &&
    retentionCount >= 1 &&
    (frequency !== 'custom' || validateCron(cronExpression));

  const handleCreate = async () => {
    if (!isValid) return;

    try {
      await createSchedule.mutateAsync({
        volumeId: volume.id,
        name,
        frequency,
        cronExpression: frequency === 'custom' ? cronExpression : undefined,
        retentionCount,
        snapshotPrefix: snapshotPrefix || name.toLowerCase().replace(/\s+/g, '-'),
      });
      toast.success('Schedule created', {
        description: `Snapshot schedule "${name}" has been created for volume "${volume.name}".`,
      });
      onOpenChange(false);
      onSuccess?.();
      // Reset form
      setName('');
      setFrequency('daily');
      setCronExpression('0 0 * * *');
      setRetentionCount(7);
      setSnapshotPrefix('');
    } catch {
      toast.error('Failed to create schedule');
    }
  };

  const getRetentionLabel = () => {
    switch (frequency) {
      case 'hourly':
        return `${retentionCount} hours`;
      case 'daily':
        return `${retentionCount} days`;
      case 'weekly':
        return `${retentionCount} weeks`;
      default:
        return `${retentionCount} snapshots`;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Schedule Snapshots
          </DialogTitle>
          <DialogDescription>
            Create automated snapshot schedule for "{volume.name}".
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Schedule Name */}
          <div className="space-y-2">
            <Label htmlFor="schedule-name">Schedule Name</Label>
            <Input
              id="schedule-name"
              placeholder="Daily Backup"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={name && !validateName(name) ? 'border-destructive' : ''}
            />
          </div>

          {/* Frequency */}
          <div className="space-y-3">
            <Label>Frequency</Label>
            <RadioGroup
              value={frequency}
              onValueChange={(v) => setFrequency(v as ScheduleFrequency)}
              className="grid grid-cols-2 gap-3"
            >
              {frequencyOptions.map((option) => (
                <label
                  key={option.value}
                  className={`flex cursor-pointer flex-col rounded-lg border p-3 transition-colors hover:bg-accent ${
                    frequency === option.value ? 'border-primary bg-accent' : ''
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value={option.value} />
                    <span className="font-medium">{option.label}</span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground pl-6">{option.description}</p>
                </label>
              ))}
            </RadioGroup>
          </div>

          {/* Custom Cron Expression */}
          {frequency === 'custom' && (
            <div className="space-y-2">
              <Label htmlFor="cron">Cron Expression</Label>
              <Input
                id="cron"
                placeholder="0 0 * * *"
                value={cronExpression}
                onChange={(e) => setCronExpression(e.target.value)}
                className={`font-mono ${
                  cronExpression && !validateCron(cronExpression) ? 'border-destructive' : ''
                }`}
              />
              <p className="text-xs text-muted-foreground">
                Format: minute hour day-of-month month day-of-week
              </p>
            </div>
          )}

          {/* Retention */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Retention Policy</Label>
              <span className="text-sm font-medium">Keep {getRetentionLabel()}</span>
            </div>
            <Slider
              value={[retentionCount]}
              onValueChange={([value]) => setRetentionCount(value)}
              min={1}
              max={frequency === 'hourly' ? 48 : frequency === 'daily' ? 30 : 12}
              step={1}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Older snapshots will be automatically deleted when this limit is reached.
            </p>
          </div>

          {/* Snapshot Prefix */}
          <div className="space-y-2">
            <Label htmlFor="prefix">Snapshot Name Prefix (optional)</Label>
            <Input
              id="prefix"
              placeholder={name.toLowerCase().replace(/\s+/g, '-') || 'scheduled'}
              value={snapshotPrefix}
              onChange={(e) => setSnapshotPrefix(e.target.value.toLowerCase())}
            />
            <p className="text-xs text-muted-foreground">
              Snapshots will be named: {snapshotPrefix || name.toLowerCase().replace(/\s+/g, '-') || 'scheduled'}-[timestamp]
            </p>
          </div>

          {/* Preview */}
          <div className="rounded-lg bg-muted/50 p-4 space-y-2 text-sm">
            <div className="flex items-center gap-2 font-medium">
              <Calendar className="h-4 w-4" />
              Schedule Preview
            </div>
            <p className="text-muted-foreground">
              {frequency === 'hourly' && 'Snapshots will be created every hour.'}
              {frequency === 'daily' && 'Snapshots will be created daily at midnight UTC.'}
              {frequency === 'weekly' && 'Snapshots will be created every Sunday at midnight UTC.'}
              {frequency === 'custom' && `Snapshots will be created according to: ${cronExpression}`}
            </p>
            <p className="text-muted-foreground">
              The {retentionCount} most recent snapshots will be retained.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={!isValid || createSchedule.isPending}>
            {createSchedule.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleSnapshotDialog;
